import React from "react";

/**
 * Parses a caption string to identify hashtags and URLs, wrapping them
 * in specific elements for later styling.
 * - Hashtags (#word) are wrapped in <span className="hashtag">
 * - URLs are wrapped in basic <a> tags.
 *
 * @param caption The caption string to parse.
 * @returns An array of React nodes with hashtags/URLs identified.
 */
export const parseCaption = (caption: string): React.ReactNode[] => {
  if (!caption) {
    return [];
  }

  // Regex to find hashtags (#word) and URLs (simplified)
  const regex = /(#\w+)|(https?:\/\/[\S]+)/g;
  const parts = caption.split(regex).filter(Boolean); // Split and remove empty/undefined parts

  return parts.map((part, index) => {
    if (part.startsWith("#")) {
      // Wrap hashtag
      return (
        <span key={index} className="hashtag">
          {part}
        </span>
      );
    } else if (part.startsWith("http")) {
      // Wrap URL in a basic link
      // The platform-specific card can style this 'a' tag later if needed
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          // Add a common class for potential basic styling or identification
          className="caption-link"
          // Prevent card clicks when clicking the link
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      );
    } else {
      // Regular text part - preserve whitespace by wrapping in span
      return <span key={index}>{part}</span>;
    }
  });
};
